import { classToPlain, Exclude, Expose, plainToClass } from "class-transformer";
import * as t from "io-ts";
import { validateJSON } from "../web/validation";
import { Entity } from "./entity";

/** Validator for a rig type. */
export const RigValidator = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    type: t.string,
    owningCompanyId: t.string,
    defaultSiteRig: t.boolean,
    active: t.boolean,
  }),
  t.partial({
    owningCompanyName: t.string,
    owningCompanyType: t.string,
    externalId: t.string,
    barcode: t.string,
    siteId: t.string,
    siteName: t.string,
    siteCompanyId: t.string,
    siteCompanyName: t.string,
    siteCompanyType: t.string,
    lastLocationDttm: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

/** Validator for an array of rig types. */
export const RigArrayValidator = t.array(RigValidator);

export type Rig = t.TypeOf<typeof RigValidator>;
export type RigArray = t.TypeOf<typeof RigArrayValidator>;

/**
 * The type of rig.
 */
export interface RigType {
  code: string;
  displayValue: string;
}

const drilling: RigType = {
  code: 'Drilling',
  displayValue: 'Drilling',
};

const coil: RigType = {
  code: 'Coil',
  displayValue: 'Coil',
};

const frac: RigType = {
  code: 'Frac',
  displayValue: 'Frac',
};

const workover: RigType = {
  code: 'Workover',
  displayValue: 'Workover',
};

/** Type of {@link Rig} for use when the type is unknown. */
export const unknownRigType: Readonly<RigType> = Object.freeze<RigType>({
  code: 'UNKNOWN',
  displayValue: 'Unknown',
});

/**
 * Types for {@link Rig}.
 */
export const rigTypes = {
  drilling,
  coil,
  frac,
  workover,
};

/**
 * Return RigType by code value.
 *
 * @param code - Code value
 * @returns {RigType} The matching RigType
 */
export const toRigType = (code: string): RigType => {
  for (const type of Object.values(rigTypes)) {
    if (type.code === code) {
      return type;
    }
  }
  return { code, displayValue: code };
};

/**
 * A rig.
 */
@Exclude()
export class RigEntity implements Entity {
  @Expose() id!: string;
  @Expose() name!: string;
  @Expose({ name: 'type' }) private _type!: string;
  @Expose() owningCompanyId!: string;
  @Expose() owningCompanyName?: string;
  @Expose() owningCompanyType?: string;
  @Expose() defaultSiteRig = false;
  @Expose() active = false;
  @Expose() externalId?: string;
  @Expose() barcode?: string;
  @Expose() siteId?: string;
  @Expose() siteName?: string;
  @Expose() siteCompanyId?: string;
  @Expose() siteCompanyName?: string;
  @Expose() siteCompanyType?: string;
  @Expose() lastLocationDttm?: number;
  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;

  readonly entityType = 'RIG';

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for RigEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a RigEntity from a Rig type value.
   *
   * @param rig The rig type value.
   */
  static from(rig: Rig): RigEntity {
    return plainToClass(RigEntity, rig, {
      excludeExtraneousValues: true,
    });
  }

  /**
   * Deserializes the RigEntity to a Rig type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<Rig> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(RigValidator, object);
  }

  get type(): RigType {
    /* istanbul ignore next */
    return toRigType(this._type);
  }

  set type(type: RigType) {
    /* istanbul ignore next */
    this._type = type.code;
  }
}
